'use client'

import React, { type ReactNode } from 'react'

import { ChevronDown } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import Listbox from '@/components/common/Listbox'
import TextOption from '@/components/common/Listbox/TextOption'

import { getAvailableFiscalYears, useDetailFilter } from './DetailFilterContext'

export interface KPICardProps {
  /** Card label */
  label: string
  /** Primary value (can be formatted currency, number, etc.) */
  value: ReactNode
  /** Optional secondary info (e.g., percentage, count) */
  secondary?: ReactNode
  /** Trend indicator */
  trend?: {
    value: number
    isPositive?: boolean
  }
}

/**
 * KPICard
 *
 * Individual stat card in the KPI strip.
 */
export const KPICard: React.FC<KPICardProps> = ({
  label,
  value,
  secondary,
  trend,
}) => {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] font-medium uppercase tracking-wider text-primary-500 sm:text-[10px] dark:text-primary-400">
        {label}
      </span>
      <div className="flex items-baseline gap-1.5">
        <span className="text-sm font-bold tabular-nums text-primary-900 sm:text-base dark:text-primary-100">
          {value}
        </span>
        {trend && (
          <span
            className={twMerge(
              'text-[10px] font-medium tabular-nums',
              trend.isPositive
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-red-600 dark:text-red-400',
            )}
          >
            {trend.isPositive ? '+' : ''}
            {trend.value.toFixed(1)}%
          </span>
        )}
      </div>
      {secondary && (
        <span className="text-[10px] tabular-nums text-primary-400 dark:text-primary-500">
          {secondary}
        </span>
      )}
    </div>
  )
}

export interface KPIStripProps {
  /** KPI cards to display */
  children: ReactNode
  /** Show fiscal year filter */
  showFiscalYearFilter?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * KPIStrip
 *
 * Horizontal strip of stat cards with optional fiscal year filter.
 * Provides at-a-glance summary metrics for the detail page.
 *
 * Layout: 4-5 stat cards + fiscal year dropdown
 */
export const KPIStrip: React.FC<KPIStripProps> = ({
  children,
  showFiscalYearFilter = true,
  className,
}) => {
  const { state, actions } = useDetailFilter()
  const fiscalYears = getAvailableFiscalYears()

  return (
    <div
      className={twMerge(
        'rounded-lg border border-primary-200 bg-white px-3 py-2 sm:px-4 sm:py-3 dark:border-primary-700 dark:bg-primary-900',
        'animate-[fadeSlideIn_300ms_ease-out_50ms_backwards]',
        className,
      )}
    >
      {/* Mobile: FY filter on top row */}
      {showFiscalYearFilter && (
        <div className="mb-2 flex items-center justify-between border-b border-primary-100 pb-1.5 sm:hidden dark:border-primary-800">
          <span className="text-[10px] font-medium uppercase tracking-wider text-primary-400 dark:text-primary-500">
            Summary Metrics
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-primary-500 dark:text-primary-400">
              FY:
            </span>
            <Listbox
              value={state.fiscalYear}
              onChange={(value: 'all' | number) => actions.setFiscalYear(value)}
            >
              <div className="relative">
                <Listbox.Button
                  className={twMerge(
                    'flex items-center gap-1.5 rounded-md border border-primary-200 bg-white px-2 py-1 text-xs font-medium',
                    'text-primary-900 dark:border-primary-600 dark:bg-primary-800 dark:text-primary-100',
                    'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500',
                    'cursor-pointer transition-colors hover:border-primary-300 dark:hover:border-primary-500',
                    'ring-0',
                  )}
                >
                  <span>
                    {state.fiscalYear === 'all'
                      ? 'All Years'
                      : `FY${state.fiscalYear}`}
                  </span>
                  <ChevronDown className="h-3 w-3 text-primary-400" />
                </Listbox.Button>
                <Listbox.Options className="mt-1 min-w-[100px]">
                  <Listbox.Option value="all">
                    <TextOption
                      text="All Years"
                      selected={state.fiscalYear === 'all'}
                    />
                  </Listbox.Option>
                  {fiscalYears.map((fy) => (
                    <Listbox.Option key={fy} value={fy}>
                      <TextOption
                        text={`FY${fy}`}
                        selected={state.fiscalYear === fy}
                      />
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            </Listbox>
          </div>
        </div>
      )}

      {/* Main content row */}
      <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4">
        {/* KPI Cards */}
        <div className="grid w-full grid-cols-2 gap-x-4 gap-y-2 sm:flex sm:w-auto sm:flex-wrap sm:gap-4 sm:divide-x sm:divide-primary-200 dark:sm:divide-primary-700">
          {React.Children.map(children, (child, index) => (
            <div className={twMerge('sm:first:pl-0', index > 0 && 'sm:pl-4')}>
              {child}
            </div>
          ))}
        </div>

        {/* Desktop: FY filter inline */}
        {showFiscalYearFilter && (
          <div className="hidden items-center gap-2 sm:flex">
            <span className="text-xs font-medium text-primary-500 dark:text-primary-400">
              FY:
            </span>
            <Listbox
              value={state.fiscalYear}
              onChange={(value: 'all' | number) => actions.setFiscalYear(value)}
            >
              <div className="relative">
                <Listbox.Button
                  className={twMerge(
                    'flex items-center gap-2 rounded-md border border-primary-200 bg-white px-3 py-1.5 text-sm font-medium',
                    'text-primary-900 dark:border-primary-600 dark:bg-primary-800 dark:text-primary-100',
                    'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500',
                    'cursor-pointer transition-colors hover:border-primary-300 dark:hover:border-primary-500',
                    'ring-0',
                  )}
                >
                  <span>
                    {state.fiscalYear === 'all'
                      ? 'All Years'
                      : `FY${state.fiscalYear}`}
                  </span>
                  <ChevronDown className="h-4 w-4 text-primary-400" />
                </Listbox.Button>
                <Listbox.Options className="mt-1 min-w-[100px]">
                  <Listbox.Option value="all">
                    <TextOption
                      text="All Years"
                      selected={state.fiscalYear === 'all'}
                    />
                  </Listbox.Option>
                  {fiscalYears.map((fy) => (
                    <Listbox.Option key={fy} value={fy}>
                      <TextOption
                        text={`FY${fy}`}
                        selected={state.fiscalYear === fy}
                      />
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            </Listbox>
          </div>
        )}
      </div>

      {/* Animation keyframes */}
      <style jsx>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  )
}
